import React, { useState, useEffect, useMemo, useRef } from "react";
import { getRiotAssetsContext, getUrl } from "@outplayed/riot-assets";
import Highcharts from "highcharts";
import HighChartsReact from "highcharts-react-official";
import { renderToString } from "react-dom/server";

const Graph = (props) => {
  const { data, proTeam, series, lineGraphColors, champImgs } = props;
  const { getChampionImg } = getRiotAssetsContext();
  const chartRef = useRef();

  const formedSeries = [];
  //Update maxes
  let max = 0;
  let timeMax = 0;
  for (const data in series) {
    const newSeries = {};
    newSeries["data"] = series[data];
    newSeries["lineWidth"] = 1;
    formedSeries.push(newSeries);
    max = Math.max(...series.flat().map((innerArray) => innerArray[1]));
    timeMax = Math.max(...series.flat().map((innerArray) => innerArray[0])) + 120000;
  }

  const options = {
    chart: {
      type: "spline",
      marginTop: 32,
      marginBottom: 45, //40
      marginLeft: 45, //51
      marginRight: 20,
      animation: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      min: 0,
      max: timeMax,
      gridLineWidth: 0,
      labels: {
        y: 30,
        formatter: function () {
          const minutes = Math.floor(this.value / 1000 / 60);
          return minutes + " min";
        },
        rotation: 0,
        style: {
          color: "#CDDCFE",
          fontFamily: "Inter",
          fontSize: "11px",
          textAlign: "center",
        },
      },
      left: 70,
      lineColor: "#070720",
      type: "datetime",
      tickLength: 0,
      tickPositioner: function () {
        const positions = [];
        if (this.dataMax != null) {
          const labelWidth = 34; // Width of each label, 33.68, 34
          const spacing = 7; // Minimum space between labels
          const numTicks = Math.floor(this.len / (labelWidth + spacing)); // Calculate the number of points that can be shown
          const tickInterval = Math.floor(this.dataMax / numTicks); //time between ticks
          for (let tick = 0; tick < this.dataMax + 40000; tick += tickInterval) {
            positions.push(tick);
          }
        }
        return positions;
      },
    },
    yAxis: {
      min: 0,
      max: max,
      gridLineColor: "#070720",
      gridZindex: 1,
      labels: {
        style: {
          color: "#CDDCFE",
          fontFamily: "Inter",
          fontSize: "11px",
          textAlign: "center",
        },
      },
      tickLength: 0,
      title: "Y AXIS",
    },

    tooltip: {
      backgroundColor: "#383864",
      borderColor: null,
      borderRadius: 2,
      borderWidth: 0,
      className: "tooltip-test",
      distance: 18,
      formatter: function () {
        const index = this.series.name;
        const src = getChampionImg(champImgs[index]);
        const yValue = Highcharts.numberFormat(this.y, 0, ".", ",");
        const xValue = Math.floor(this.x / 1000 / 60) + " min";

        return renderToString(
          <div className="tooltip">
            <div className="left-half">
              <div className="tt-champion-wrapper" style={{ border: `1px solid ${lineGraphColors[index]}` }}>
                <div className="tt-border-color">
                  <img src={src} />
                </div>
              </div>
            </div>
            <div className="right-half">
              <div className="y-value">{yValue}</div>
              <div className="x-value">{xValue}</div>
            </div>
          </div>,
        );
      },
      headerFormat: "",
      style: {
        textAlign: "center",
      },
      useHTML: true,
    },

    series: formedSeries.map((seriesObj, index) => ({
      color: lineGraphColors[index],
      data: seriesObj.data,
      lineWidth: 1,
      marker: {
        enabled: false,
        symbol: "circle",
      },
      name: index,
      states: {
        hover: {
          lineWidthPlus: 2,
        },
      },
    })),

    legend: {
      enabled: false,
    },
  };

  const chartCallback = (chart) => {
    if (chart) {
      chart.reflow();
    }
  };

  return (
    <div className="graph-container">
      <HighChartsReact
        highcharts={Highcharts}
        ref={chartRef}
        options={options}
        containerProps={{
          style: { width: "100%", height: "100%" },
        }}
        callback={chartCallback}
      />
    </div>
  );
};

const Metrics = (props) => {
  const { data } = props;
  const { getChampionImgFromSprite } = getRiotAssetsContext();
  const teamOne = data?.match.matchSummary.teamA;
  const teamTwo = data?.match.matchSummary.teamB;
  const allPlayers = [...data?.match.matchSummary.teamA, ...data?.match.matchSummary.teamB];
  const borderColors = [
    "#B8E986",
    "#D155FF",
    "#BECCFA",
    "#DBA54B",
    "#00FF93",
    "#8C3344",
    "#FF8335",
    "#7878B5",
    "#0ECD2C",
    "#32B2FA",
  ];
  const allChampionStats = splitListByPid(data?.match.historicalData.metricsData);
  const mainSummoner = data?.match.playerInfo?.summonerName;

  const [series, setSeries] = useState(data?.match.historicalData.metricsData);

  const [selectedChampions, setSelectedChampions] = useState({
    0: data?.match.matchSummary.teamA[0].summonerName === mainSummoner,
    1: data?.match.matchSummary.teamA[1].summonerName === mainSummoner,
    2: data?.match.matchSummary.teamA[2].summonerName === mainSummoner,
    3: data?.match.matchSummary.teamA[3].summonerName === mainSummoner,
    4: data?.match.matchSummary.teamA[4].summonerName === mainSummoner,
    5: data?.match.matchSummary.teamB[0].summonerName === mainSummoner,
    6: data?.match.matchSummary.teamB[1].summonerName === mainSummoner,
    7: data?.match.matchSummary.teamB[2].summonerName === mainSummoner,
    8: data?.match.matchSummary.teamB[3].summonerName === mainSummoner,
    9: data?.match.matchSummary.teamB[4].summonerName === mainSummoner,
  });

  const [toggledCategory, setToggledCategory] = useState({
    totalGold: true,
    totalDamageDoneToChampions: false,
    xp: false,
    cs: false,
    carry: false,
  });

  const [lineGraphColors, setLineGraphColors] = useState(["B8E986"]);
  const [champImgs, setChampImgs] = useState({ 0: 42 });

  function toggleChampion(champNumber) {
    setSelectedChampions((prevSelectedChampions) => ({
      ...prevSelectedChampions,
      [champNumber]: !prevSelectedChampions[champNumber],
    }));
  }

  function setCategory(category) {
    setToggledCategory((prevSelectedCategory) => {
      const updatedCategory = {};

      for (const key in prevSelectedCategory) {
        updatedCategory[key] = false;
      }

      updatedCategory[category] = true;
      return updatedCategory;
    });
  }

  function findSummonerTeam(mainSummoner) {
    const firstHalf = allPlayers.slice(0, 5);
    if (firstHalf.includes(mainSummoner)) {
      return 1;
    }
    return 2;
  }

  function splitListByPid(list) {
    const sublistLength = list.length / 10;
    const sublistCount = Math.ceil(list.length / sublistLength);
    const sublists = [];

    for (let i = 0; i < sublistCount; i++) {
      const startIndex = i * sublistLength;
      const endIndex = startIndex + sublistLength;
      const sublist = list.slice(startIndex, endIndex);
      sublists.push(sublist);
    }

    return sublists;
  }

  useEffect(() => {
    let tempColorLines = [];
    for (const champ in selectedChampions) {
      if (selectedChampions[champ]) {
        tempColorLines.push(borderColors[champ]);
      }
    }
    setLineGraphColors(tempColorLines);
  }, [selectedChampions]);

  useEffect(() => {
    let tempChampImgs = {};
    let index = 0;
    for (const champ in selectedChampions) {
      if (selectedChampions[champ] && champ <= 4) {
        tempChampImgs[index] = teamOne[champ].championId;
        index++;
      }
      if (selectedChampions[champ] && champ >= 5) {
        tempChampImgs[index] = teamTwo[champ - 5].championId;
        index++;
      }
    }
    setChampImgs(tempChampImgs);
  }, [selectedChampions]);

  useEffect(() => {
    let finalChampionSeries = [];
    let finalSeries = [];
    let selectedStat;
    //Get selected stat
    for (const stat in toggledCategory) {
      if (toggledCategory[stat]) {
        selectedStat = stat;
        break;
      }
    }
    //Filter champions
    for (const allData in allChampionStats) {
      if (selectedChampions[allData]) {
        finalChampionSeries.push(allChampionStats[allData]);
      }
    }
    //Filter selected stats in correct format
    for (const champData in finalChampionSeries) {
      const championStats = [];
      for (const eventData in finalChampionSeries[champData]) {
        championStats.push([
          finalChampionSeries[champData][eventData].timestamp,
          finalChampionSeries[champData][eventData][selectedStat],
        ]);
      }
      finalSeries.push(championStats);
    }
    setSeries(finalSeries);
  }, [selectedChampions, toggledCategory]);

  return (
    <div className="metrics-container">
      <div className="metrics-holder">
        <div className="metric-line" onClick={() => setCategory("totalGold")}>
          <div className={`metric ${toggledCategory.totalGold ? "active" : ""}`}>Gold</div>
          {toggledCategory.totalGold && <div className="active-line" />}
        </div>
        <div className="metric-line" onClick={() => setCategory("totalDamageDoneToChampions")}>
          <div className={`metric ${toggledCategory.damage ? "active" : ""}`}>Damage</div>
          {toggledCategory.totalDamageDoneToChampions && <div className="active-line" />}
        </div>
        <div className="metric-line" onClick={() => setCategory("xp")}>
          <div className={`metric ${toggledCategory.xp ? "active" : ""}`}>Exp</div>
          {toggledCategory.xp && <div className="active-line" />}
        </div>
        <div className="metric-line" onClick={() => setCategory("cs")}>
          <div className={`metric ${toggledCategory.cs ? "active" : ""}`}>CS</div>
          {toggledCategory.cs && <div className="active-line" />}
        </div>
        {/* Add this back later once we get carry score */}
        {/* <div className="metric-line" onClick={() => setCategory("carry")}>
          <div className={`metric ${toggledCategory.carry ? "active" : ""}`}>Carry Score</div>
          {toggledCategory.carry && <div className="active-line" />}
        </div> */}
      </div>
      <div className="metrics-bottom">
        <div className="summoner-champions">
          <div className="team">
            {teamOne.map((summoner, index) => {
              return (
                <div
                  key={index}
                  className="champion-wrapper"
                  style={{ border: selectedChampions[index] ? `1px solid ${borderColors[index]}` : "" }}
                  onClick={() => toggleChampion(index)}
                >
                  <div className="border-color">
                    <div className="champion-icon" style={{ opacity: !selectedChampions[index] ? "0.3" : "" }}>
                      {getChampionImgFromSprite(summoner.championId, { size: 34 })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="team">
            {teamTwo.map((summoner, index) => {
              return (
                <div
                  key={index}
                  className="champion-wrapper"
                  style={{ border: selectedChampions[index + 5] ? `1px solid ${borderColors[index + 5]}` : "" }}
                  onClick={() => toggleChampion(index + 5)}
                >
                  <div className="border-color">
                    <div className="champion-icon" style={{ opacity: !selectedChampions[index + 5] ? "0.3" : "" }}>
                      {getChampionImgFromSprite(summoner.championId, { size: 34 })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <Graph
          data={data}
          proTeam={findSummonerTeam(mainSummoner)}
          series={series}
          lineGraphColors={lineGraphColors}
          champImgs={champImgs}
        />
      </div>
    </div>
  );
};

export default Metrics;
